import React, { useCallback } from 'react';
import isNumber from 'lodash/isNumber';

import { useTranslation } from '@lyearn/i18n';
import { Stack } from '@lyearn/ui';
import NumberInput from '@/components/NumberInput';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { RegistrationSettingsInput } from '@/types/schema';

function RegistrationSettings({
  disabled = false,
  allowMultiSlotRegistration = false,
  allowEnableWaitingRoom = false,
  enableWaitingRoom,
  registrationSettings,
  onChange,
  onChangeEnableWaitingRoom,
}: {
  disabled?: boolean;
  allowMultiSlotRegistration?: boolean;
  allowEnableWaitingRoom?: boolean;
  enableWaitingRoom?: boolean | undefined | null;
  registrationSettings?: RegistrationSettingsInput;
  onChange: (value?: RegistrationSettingsInput) => void;
  onChangeEnableWaitingRoom: (val: boolean) => void;
}) {
  const { t } = useTranslation();
  const isSeatLimitedEnabled = isNumber(registrationSettings?.seatLimit);

  const onChangeSeatLimit = useCallback(
    (value: number) => onChange({ ...registrationSettings, seatLimit: value }),
    [registrationSettings, onChange],
  );

  return (
    <Stack className="px-8" direction="col" gapY={24}>
      <SwitchWithInfo
        checked={!!registrationSettings}
        description={t('Events:Editor.RequiresRegistrationDescription')}
        disabled={disabled}
        label={t('Events:Editor.RequiresRegistration')}
        onChange={(value: boolean) => onChange(value ? {} : undefined)}
      />
      {allowMultiSlotRegistration ? (
        <SwitchWithInfo
          checked={Boolean(registrationSettings?.multiSlotRegistration)}
          description={t('Events:Editor.MultiSlotRegistrationDescription')}
          disabled={disabled}
          label={t('Events:Editor.MultiSlotRegistration')}
          onChange={(value: boolean) =>
            onChange({ ...registrationSettings, multiSlotRegistration: value })
          }
        />
      ) : null}
      <SwitchWithInfo
        checked={isSeatLimitedEnabled}
        description={t('Events:Editor.LimitClassCapacityDescription')}
        disabled={disabled}
        label={t('Events:Editor.LimitClassCapacity')}
        onChange={(value: boolean) =>
          onChange(
            value
              ? { ...registrationSettings, seatLimit: 30 }
              : { ...registrationSettings, seatLimit: undefined },
          )
        }
      />
      {isSeatLimitedEnabled ? (
        <NumberInput
          className="w-192"
          disabled={disabled}
          inputSuffix={t('Events:Editor.seats')}
          min={1}
          value={registrationSettings?.seatLimit ?? 0}
          onChange={onChangeSeatLimit}
        />
      ) : null}
      {allowEnableWaitingRoom ? (
        <SwitchWithInfo
          checked={enableWaitingRoom ?? false}
          description={t('Events:Editor.GiveWaitingRoomDescription')}
          disabled={disabled}
          label={t('Events:Editor.GiveWaitingRoom')}
          onChange={onChangeEnableWaitingRoom}
        />
      ) : null}
    </Stack>
  );
}

export default React.memo(RegistrationSettings);
